import config from "../config";

const PROMISES = [
  {
    symbol: "I",
    title: "Purity in Every Product",
    desc: "No shortcuts, no compromises — only the cleanest ingredients nature offers.",
  },
  {
    symbol: "II",
    title: "Sustainability in Every Step",
    desc: "From seed to shelf, we honour the earth that feeds us all.",
  },
  {
    symbol: "III",
    title: "Trust in Every Transaction",
    desc: "Transparent sourcing, honest pricing, and relationships built to last.",
  },
];

const GALLERY = [
  { src: "/images/about-1.jpg", alt: "Freshly harvested millets and grains" },
  { src: "/images/about-4.jpg", alt: "Golden cold-pressed oil" },
  { src: "/images/about-3.jpg", alt: "Pure raw honey and natural sweeteners" },
  { src: "/images/about-2.jpg", alt: "Sunrise over organic farmland" },
];

export default function About() {
  return (
    <section className="rk-about-section" style={s.section}>
      <div style={s.bgTexture} />

      <div className="rk-about-inner" style={s.inner}>

        {/* ── LEFT: Mosaic gallery ── */}
        <div style={s.left}>
          <div className="rk-about-mosaic" style={s.mosaic}>

            <div style={{ ...s.tile, ...s.tileTall }} className="rk-tile">
              <img src={GALLERY[0].src} alt={GALLERY[0].alt} style={s.tileImg} loading="lazy" />
              <div style={s.tileOverlay} />
              <span style={s.tileCaption}>Farm-fresh since day one</span>
            </div>

            <div style={s.tileCol}>
              <div style={{ ...s.tile, ...s.tileHalf }} className="rk-tile">
                <img src={GALLERY[1].src} alt={GALLERY[1].alt} style={s.tileImg} loading="lazy" />
                <div style={s.tileOverlay} />
              </div>
              <div style={{ ...s.tile, ...s.tileHalf }} className="rk-tile">
                <img src={GALLERY[2].src} alt={GALLERY[2].alt} style={s.tileImg} loading="lazy" />
                <div style={s.tileOverlay} />
              </div>
            </div>

            <div style={{ ...s.tile, ...s.tileWide }} className="rk-tile">
              <img src={GALLERY[3].src} alt={GALLERY[3].alt} style={s.tileImg} loading="lazy" />
              <div style={s.tileOverlay} />
              <span style={s.tileCaption}>Harvest of trust</span>
            </div>

          </div>

          {/* Stat cards */}
          <div className="rk-about-stats" style={s.statsRow}>
            {[
              { num: "100%", label: "Organic Certified" },
              { num: "10+",  label: "Product Varieties" },
              { num: "10K+", label: "Happy Families" },
            ].map(({ num, label }) => (
              <div key={label} className="rk-about-stat-card" style={s.statCard}>
                <span className="rk-about-stat-num" style={s.statNum}>{num}</span>
                <span className="rk-about-stat-label" style={s.statLabel}>{label}</span>
              </div>
            ))}
          </div>
        </div>

        {/* ── RIGHT: Text content ── */}
        <div className="rk-about-right" style={s.right}>
          <p style={s.eyebrow}>— Our Story —</p>
          <h2 style={s.title}>About {config.storeName}</h2>

          <div style={s.ornament}>
            <span style={s.ornLine} />
            <span style={s.ornDiamond} />
            <span style={s.ornLine} />
          </div>

          <blockquote className="rk-about-tagline" style={s.tagline}>
            "From Earth. With Care. For You."
          </blockquote>

          <p className="rk-about-body" style={s.body}>
            {config.storeName} began its journey with a vision to deliver organic,
            healthy, and nutritional products for your daily life. We blend
            traditional farming methods with careful processing to bring nature's
            goodness straight to your doorstep.
          </p>
          <p className="rk-about-body" style={s.body}>
            Today we are a trusted producer of quality millets, cold-pressed oils,
            and natural sweeteners — committed to transparency, fairness, and
            sustainability in every step we take.
          </p>

          <div style={s.promiseWrap}>
            <p style={s.promiseHeading}>Our Promise</p>
            {PROMISES.map(({ symbol, title, desc }) => (
              <div key={symbol} style={s.promiseItem}>
                <div style={s.promiseSymbol}>{symbol}</div>
                <div>
                  <p style={s.promiseTitle}>{title}</p>
                  <p style={s.promiseDesc}>{desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Cinzel:wght@400;600;700&family=Cormorant+Garamond:ital,wght@0,400;0,500;0,600;1,400;1,500&family=Cormorant+SC:wght@400;500;600&display=swap');

        @keyframes aboutFade {
          from { opacity: 0; transform: translateY(24px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        .rk-tile img { transition: transform 0.6s ease; }
        .rk-tile:hover img { transform: scale(1.06); }

        /* ── Tablet ── */
        @media (max-width: 1024px) {
          .rk-about-inner {
            gap: 40px !important;
          }
        }

        /* ── Mobile: single column ── */
        @media (max-width: 767px) {
          .rk-about-section {
            padding: 48px 20px !important;
          }
          .rk-about-inner {
            grid-template-columns: 1fr !important;
            gap: 28px !important;
          }
          .rk-about-mosaic {
            grid-template-rows: 210px 150px !important;
          }
          .rk-about-stats {
            gap: 8px !important;
          }
          .rk-about-stat-card {
            padding: 12px 8px !important;
          }
          .rk-about-stat-num {
            font-size: 1.2rem !important;
          }
          .rk-about-stat-label {
            font-size: 0.6rem !important;
            letter-spacing: 1.2px !important;
          }
          .rk-about-tagline {
            font-size: 1.05rem !important;
          }
          .rk-about-body {
            font-size: 0.97rem !important;
          }
          .rk-about-right {
            animation: none !important;
          }
        }

        /* ── Very small ── */
        @media (max-width: 400px) {
          .rk-about-mosaic {
            grid-template-rows: 180px 130px !important;
          }
          .rk-about-stat-label {
            font-size: 0.55rem !important;
            letter-spacing: 1px !important;
          }
        }
      `}</style>
    </section>
  );
}

/* ─────────────── styles ─────────────── */
const FONT_DISPLAY = "'Cinzel', 'Times New Roman', serif";
const FONT_BODY    = "'Cormorant Garamond', Georgia, serif";
const FONT_CAPS    = "'Cormorant SC', Georgia, serif";

const s = {
  section: {
    position: "relative",
    background: "#c49a10",
    padding: "88px 40px",
    overflow: "hidden",
    boxSizing: "border-box",
  },
  bgTexture: {
    position: "absolute",
    inset: 0,
    backgroundImage:
      "radial-gradient(ellipse at 10% 60%, rgba(45,122,28,0.1) 0%, transparent 55%)," +
      "radial-gradient(ellipse at 90% 20%, rgba(74,173,46,0.07) 0%, transparent 50%)",
    pointerEvents: "none",
  },
  inner: {
    position: "relative",
    zIndex: 2,
    maxWidth: 1160,
    margin: "0 auto",
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: 72,
    alignItems: "start",
  },
  left: {
    display: "flex",
    flexDirection: "column",
    gap: 16,
    animation: "aboutFade 0.8s ease 0.05s both",
  },
  mosaic: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gridTemplateRows: "260px 180px",
    gap: 10,
  },
  tileCol: {
    display: "flex",
    flexDirection: "column",
    gap: 10,
  },
  tile: {
    position: "relative",
    overflow: "hidden",
    borderRadius: 4,
    cursor: "pointer",
  },
  tileTall: { gridRow: "1 / 2" },
  tileHalf: { flex: 1, minHeight: 0 },
  tileWide: { gridColumn: "1 / 3" },
  tileImg: {
    width: "100%",
    height: "100%",
    objectFit: "cover",
    display: "block",
    transition: "transform 0.6s ease",
  },
  tileOverlay: {
    position: "absolute",
    inset: 0,
    background: "linear-gradient(to top, rgba(8,18,6,0.65) 0%, transparent 60%)",
    pointerEvents: "none",
  },
  tileCaption: {
    position: "absolute",
    bottom: 12,
    left: 14,
    fontFamily: FONT_CAPS,
    fontSize: "0.72rem",
    fontWeight: 500,
    letterSpacing: "2px",
    color: "rgba(240,237,230,0.75)",
    textTransform: "uppercase",
  },
  statsRow: {
    display: "grid",
    gridTemplateColumns: "repeat(3,1fr)",
    gap: 10,
  },
  statCard: {
    background: "rgba(255,255,255,0.04)",
    border: "1px solid rgba(126,200,80,0.18)",
    borderRadius: 4,
    padding: "16px 12px",
    textAlign: "center",
  },
  statNum: {
    display: "block",
    fontFamily: FONT_DISPLAY,
    fontSize: "1.5rem",
    fontWeight: 700,
    color: "#10100f",
    letterSpacing: "1px",
    lineHeight: 1.1,
    marginBottom: 5,
  },
  statLabel: {
    fontFamily: FONT_CAPS,
    fontSize: "0.65rem",
    fontWeight: 500,
    letterSpacing: "1.8px",
    color: "rgba(240,237,230,0.5)",
    textTransform: "uppercase",
  },
  right: {
    animation: "aboutFade 0.8s ease 0.18s both",
  },
  eyebrow: {
    fontFamily: FONT_CAPS,
    fontSize: "0.75rem",
    fontWeight: 500,
    letterSpacing: "4px",
    color: "#0d0d0d",
    margin: "0 0 14px",
    textTransform: "uppercase",
  },
  title: {
    fontFamily: FONT_DISPLAY,
    fontSize: "clamp(1.6rem, 2.8vw, 2.4rem)",
    fontWeight: 700,
    color: "#f0ede6",
    margin: "0 0 18px",
    letterSpacing: "1.5px",
    lineHeight: 1.2,
  },
  ornament: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    marginBottom: 22,
    width: 140,
  },
  ornLine: {
    flex: 1,
    height: 1,
    background: "rgba(245,247,244,0.4)",
    display: "block",
  },
  ornDiamond: {
    display: "block",
    width: 6,
    height: 6,
    background: "#0c0c0c",
    transform: "rotate(45deg)",
    flexShrink: 0,
  },
  tagline: {
    fontFamily: FONT_BODY,
    fontSize: "1.25rem",
    fontStyle: "italic",
    color: "rgba(240,237,230,0.8)",
    margin: "0 0 22px",
    lineHeight: 1.6,
    borderLeft: "2px solid rgba(246,248,244,0.4)",
    paddingLeft: 16,
  },
  body: {
    fontFamily: FONT_BODY,
    fontSize: "1.05rem",
    color: "rgba(240,237,230,0.6)",
    lineHeight: 1.85,
    margin: "0 0 14px",
    fontWeight: 400,
  },
  promiseWrap: {
    margin: "32px 0 36px",
    borderTop: "1px solid rgba(16,16,15,0.15)",
    paddingTop: 28,
  },
  promiseHeading: {
    fontFamily: FONT_DISPLAY,
    fontSize: "0.72rem",
    fontWeight: 600,
    letterSpacing: "3px",
    color: "#0c0c0c",
    textTransform: "uppercase",
    margin: "0 0 20px",
  },
  promiseItem: {
    display: "flex",
    gap: 18,
    alignItems: "flex-start",
    marginBottom: 18,
  },
  promiseSymbol: {
    fontFamily: FONT_DISPLAY,
    fontSize: "0.7rem",
    fontWeight: 600,
    letterSpacing: "2px",
    color: "#0c0c0c",
    border: "1px solid rgba(251,252,251,0.4)",
    borderRadius: 2,
    padding: "5px 9px",
    flexShrink: 0,
    marginTop: 2,
  },
  promiseTitle: {
    fontFamily: FONT_CAPS,
    fontSize: "0.88rem",
    fontWeight: 600,
    letterSpacing: "1.2px",
    color: "#f0ede6",
    margin: "0 0 4px",
    textTransform: "uppercase",
  },
  promiseDesc: {
    fontFamily: FONT_BODY,
    fontSize: "0.95rem",
    fontStyle: "italic",
    color: "rgba(240,237,230,0.5)",
    margin: 0,
    lineHeight: 1.6,
  },
  cta: {
    fontFamily: FONT_DISPLAY,
    fontSize: "0.72rem",
    fontWeight: 600,
    letterSpacing: "3px",
    textTransform: "uppercase",
    color: "#f0ede6",
    background: "transparent",
    border: "1px solid rgba(126,200,80,0.5)",
    borderRadius: 2,
    padding: "14px 34px",
    cursor: "pointer",
    transition: "background 0.28s ease, color 0.28s ease, border-color 0.28s ease",
  },
};